// frontend/src/components/homepage/FeaturesLive.js
//
// "יתרונות" - the eight-tile features bento. A literal port of the prototype's
// `<section class="features">`: the centred head, then 8 numbered tiles in a
// 4-column grid (2 columns under 1100px, 1 under 600px), each with a 160px
// artefact strip on top and its copy below. Three tiles carry a real link.
//
// COPY IS THE PROTOTYPE'S, VERBATIM, and lives in ONE table (`TILES`) so a
// reword is one edit. The prototype is Hebrew-only; there is no English copy
// to switch to, so `isHebrew` is accepted for the section contract and the
// Hebrew renders either way (flagged in the port report).
//
// EACH TILE IS ITS OWN REVEAL, like the prototype's per-tile `data-reveal`.
// The prototype's `--d` stagger on tiles 02/03/05/06/08 is INERT there -
// `.tile`'s own `transition` shorthand outranks the reveal's and resets the
// delay. Owner round 2 made the stagger REAL: featuresLive.css re-applies
// `transition-delay: var(--hl-d)` on `.hl-ft-tile.hl-reveal`. Because the
// tile's transition list is shared with its hover lift, the delay is dropped
// (the inline `--hl-d` removed) the moment the reveal's transform transition
// ends - otherwise every hover on a staggered tile would start .08/.16s late.
// Under motion-off / prerender no transition ever ends, so the delay is never
// set in the first place.
//
// THE SPOTLIGHT (`data-spot`): a radial glow under the pointer, drawn by the
// tile's `::before` from `--hl-ft-mx/--hl-ft-my`. The handler writes the two
// custom properties straight onto the element - no state, no re-render - and
// is a no-op under motion-off (a pointer-following glow is a moving part) so
// the glow then sits centred, which is the CSS default.
//
// The artefacts are decorative: every one is `aria-hidden` and memo'd. Three of
// them carry a PROFESSION, so a profession page must re-dress them
// (owner, 2026-09-22): `profession` overrides the push alert's first line
// (tile 03), the chips and their cycle deck (tile 05) and the drafted reply
// (tile 06). The homepage passes no `profession` and renders verbatim.

import React, { useCallback, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import SectionShell from "./SectionShell";
import Arrow from "./Arrow";
import useReveal from "../../motion/useReveal";
import useMotion from "../../motion/useMotion";
import AiBrain from "./artefacts/AiBrain";
import WebsiteMock from "./artefacts/WebsiteMock";
import PushStack from "./artefacts/PushStack";
import SupporterNet from "./artefacts/SupporterNet";
import MultiChips from "./artefacts/MultiChips";
import AiDraft from "./artefacts/AiDraft";
import MobileMock from "./artefacts/MobileMock";
import BiRadar from "./artefacts/BiRadar";
import { PROFESSIONAL_MAX_CATEGORIES } from "../../contexts/PricingContext";
import "../../homepage/featuresLive.css";

/** The head block - one constant, owner-reworded in place. */
export const HEADING = {
  he: {
    eyebrow: "יתרונות",
    title: "כל מה שצריך, במקום אחד",
    subLine1: "פלטפורמה חכמה לניטור וזיהוי הזדמנויות עסקיות",
    subLine2: "שעובדת בשבילך סביב השעון",
  },
};

/**
 * The eight tiles, in grid order. `delay` is the prototype's `--d` (see the
 * header note), `id` the anchor a tile keeps, `more` the tile's link.
 */
export const TILES = [
  {
    key: "ai",
    num: "01",
    artClass: "hl-ft-ai",
    Art: AiBrain,
    title: "AI חכם ומתקדם",
    desc: "הבינה המלאכותית שלנו סורקת אלפי פוסטים ביום, מנתחת ומזהה את כל ההזדמנויות העסקיות הרלוונטיות עבורך.",
    more: { label: "על סריקת קבוצות פייסבוק", to: "/facebook-group-scanning" },
  },
  {
    key: "web",
    num: "02",
    delay: ".08s",
    artClass: "hl-ft-web",
    Art: WebsiteMock,
    title: "אתר עסקי בכתובת אישית",
    desc: "אתר עסקי מרהיב בכתובת משלך, גלריה, טופס יצירת קשר וכרטיס ביקור דיגיטלי עם קוד QR - מוכן בדקות, בלי מעצב ובלי מפתח.",
    more: { label: "ראה עוד", to: "/business-website" },
  },
  {
    key: "push",
    num: "03",
    delay: ".16s",
    artClass: "hl-ft-push",
    Art: PushStack,
    title: "התראות Push ומייל בזמן אמת",
    desc: "מעקב רציף והתראות מיידיות - לנייד ללא הגבלה יומית, וגם למייל. כל הזדמנות עסקית רלוונטית, לקוח פוטנציאלי ותזכורות follow-up מגיעות אלייך ישירות.",
  },
  {
    key: "net",
    num: "04",
    id: "supporter-network",
    artClass: "hl-ft-net",
    Art: SupporterNet,
    title: "רשת תומכים אישית",
    desc: "צרפו לקוחות מרוצים ומכרים שממליצים עליכם בתגובות לפוסטים רלוונטיים - הוכחה חברתית בדיוק ברגע שהלקוח מחפש.",
    more: { label: "ראה עוד", to: "/supporter-network" },
  },
  {
    key: "multi",
    num: "05",
    delay: ".08s",
    artClass: "hl-ft-multi",
    Art: MultiChips,
    title: "כיסוי מרובה מקצועות ואזורים",
    // The count is DERIVED (CLAUDE.md "every advertised plan number"), from
    // the offline mirror the backend pins against tier_config. Wording is the
    // owner's, unchanged.
    desc: `עקוב במקביל אחר ${PROFESSIONAL_MAX_CATEGORIES} מקצועות / תחומים עסקיים שונים, בכל אזורי הפעילות שלך - ולא תפספס אף הזדמנות רלוונטית.`,
  },
  {
    key: "draft",
    num: "06",
    delay: ".16s",
    artClass: "hl-ft-draft",
    Art: AiDraft,
    title: "ניסוח פניות אישיות באמצעות AI",
    desc: "לכל הזדמנות שמתגלה, ה-AI מנסח עבורך הודעת פנייה מותאמת אישית - מוכנה לשליחה, חוסכת זמן ומגדילה את אחוז המענה.",
  },
  {
    key: "mob",
    num: "07",
    artClass: "hl-ft-mob",
    Art: MobileMock,
    title: "אפליקציית מובייל מתקדמת",
    desc: "אפליקציה חכמה ומותאמת במיוחד לסמארטפון - נהל את כל הלידים שלך, קבל התראות push בזמן אמת וגש לכל המידע בקלות, מכל מקום ובכל זמן.",
  },
  {
    key: "bi",
    num: "08",
    delay: ".08s",
    artClass: "hl-ft-bi",
    Art: BiRadar,
    title: "מודיעין עסקי. נוכחות. שליטה.",
    desc: "ניטור אזכורי מותג ומתחרים, זיהוי שיח רלוונטי, התראות על פוסטים חריגים והגברת שיח ונוכחות אורגנית - כל מה שצריך לבנייה ושמירה על מוניטין דיגיטלי חזק.",
  },
];

/** One tile: its own reveal, its own spotlight, its artefact, its copy. */
function Tile({ tile, artProps }) {
  const { ref: revealRef, className: revealClass } = useReveal();
  const { off: motionOff } = useMotion();
  const elRef = useRef(null);
  // The stagger delay lives only until the reveal has run (see the header).
  const [staggerDone, setStaggerDone] = useState(false);

  const onTransitionEnd = useCallback((event) => {
    if (event.target === elRef.current && event.propertyName === "transform") {
      setStaggerDone(true);
    }
  }, []);

  const setRefs = useCallback(
    (node) => {
      elRef.current = node;
      revealRef(node);
    },
    [revealRef]
  );

  const onPointerMove = useCallback(
    (event) => {
      const el = elRef.current;
      if (!el || motionOff) return;
      const rect = el.getBoundingClientRect();
      el.style.setProperty("--hl-ft-mx", `${event.clientX - rect.left}px`);
      el.style.setProperty("--hl-ft-my", `${event.clientY - rect.top}px`);
    },
    [motionOff]
  );

  const { Art } = tile;
  const className = revealClass
    ? `hl-ft-tile ${tile.artClass} ${revealClass}`
    : `hl-ft-tile ${tile.artClass}`;

  return (
    <article
      ref={setRefs}
      id={tile.id}
      className={className}
      data-i={tile.num}
      style={
        tile.delay && !staggerDone && !motionOff ? { "--hl-d": tile.delay } : undefined
      }
      onPointerMove={onPointerMove}
      onTransitionEnd={onTransitionEnd}
    >
      {/* Owner round 3: the 01-08 corner badge is gone. `data-i` stays - it is
          the tile's ordinal for tests and for any future styling hook, and it
          costs nothing; only the rendered glyph was removed. */}
      <div className="hl-ft-art" aria-hidden="true">
        <Art {...artProps} />
      </div>
      <div className="hl-ft-bd">
        <h3>{tile.title}</h3>
        <p>{tile.desc}</p>
        {tile.more && (
          <RouterLink className="hl-ft-more" to={tile.more.to}>
            {/* The prototype's literal "... ←" as ONE inline run (the arrow is
                the shared SVG, Arrow.js): a single child of the inline-flex
                link, so the arrow wraps with the words exactly as the glyph
                did rather than becoming a second flex item. One string for
                the label + space: adjacent text nodes cannot be hydrated. */}
            <span>
              {`${tile.more.label} `}
              <Arrow direction="left" />
            </span>
          </RouterLink>
        )}
      </div>
    </article>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.isHebrew] section contract; the copy is Hebrew-only
 *   today (no English exists in the prototype).
 * @param {{pushAlert: string, chips: string[], chipPool: string[], draft: string}} [props.profession]
 *   a profession page's own push line, chips + cycle deck and drafted reply.
 */
export default function FeaturesLive({ isHebrew = true, profession }) {
  // Hebrew-only today - see the header note; `isHebrew` is the section contract.
  const t = HEADING.he;
  const head = useReveal();
  // Per-tile props for the three profession-bearing artefacts; none on the
  // homepage. Keyed by tile key so the TILES table stays the one copy source.
  const artPropsByKey = profession
    ? {
        push: { hotText: profession.pushAlert },
        multi: { labels: profession.chips, pool: profession.chipPool },
        draft: { text: profession.draft },
      }
    : {};

  return (
    <SectionShell id="features" scene="features" mesh="disperse" meshAlpha=".25">
      <div
        ref={head.ref}
        className={
          head.className
            ? `hl-head hl-head-center hl-ft-head ${head.className}`
            : "hl-head hl-head-center hl-ft-head"
        }
      >
        <div className="hl-eyebrow">{t.eyebrow}</div>
        <h2 className="hl-h2 hl-h2-center">{t.title}</h2>
        <p className="hl-sub hl-sub-center">
          {t.subLine1}
          <br />
          {t.subLine2}
        </p>
      </div>
      <div className="hl-ft-bento">
        {TILES.map((tile) => (
          <Tile key={tile.key} tile={tile} artProps={artPropsByKey[tile.key]} />
        ))}
      </div>
    </SectionShell>
  );
}
